import { handleActions } from 'redux-actions';

import loadingStatuses from 'src/constants/loadingStatuses';
import { updateItemInListByIdHelper } from 'src/helpers/commonHelpers';
import {
  changeCurrentWorkspace,
  changeWorkspace,
  createWorkspace,
  deleteWorkspace,
  fetchWorkspaces,
  setWorkspaces,
} from '@redux/actions/workspaces.actions';
import { WorkspaceShortType } from '@ts/userOrganization.types';
import { pushIFrameUrl } from '@api/semrush/integration.api';
import urls, { urlPrefixWithoutWorkspace } from '@constants/urls';
import { isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import { isSemrushMode } from '@constants/app-modes';

export type WorkspacesStateType = {
  currentWorkspaceUid?: number;
  workspaces: WorkspaceShortType[];
  loadingStatus: string;
};

const initialState: WorkspacesStateType = {
  currentWorkspaceUid: null,
  workspaces: [],
  loadingStatus: loadingStatuses.PENDING,
};

const updateWorkspaceUrl = (payload: WorkspaceShortType[]): number => {
  const { history } = window;

  const { pathname } = window.location;
  const params = pathname.split('/');
  const hasOptionalWorkspace = params[2];
  const startOfUrl = params[1];
  const workspaceId = parseInt(hasOptionalWorkspace, 10);

  const activeWorkspace =
    payload.find((item) => item.id === workspaceId) ||
    payload.find((item) => item.favorite) ||
    payload[0];

  const isExclusion = urlPrefixWithoutWorkspace.find((item) => item === startOfUrl);

  function mainPageUrl(id: number) {
    if (isConciergeRealm() || isPublisherRealm()) {
      return urls.ORDERS(id);
    }
    return urls.ALL_CAMPAIGNS(id);
  }

  // Also rewrite when the URL segment parses to <= 0. `/workspaces/0/...` appears
  // after AppSumo first-login when the register response defaults workspaceId to
  // 0 (proto3 unset). `parseInt("0")` is 0 (not NaN), so without this guard the
  // reducer would leave the broken URL in place.
  if (!isExclusion) {
    if (Number.isNaN(workspaceId) || workspaceId <= 0) {
      const url = mainPageUrl(activeWorkspace.id);
      history.replaceState(null, '', url);
      if (isSemrushMode()) {
        pushIFrameUrl(url);
      }
    }
  }

  return activeWorkspace.id;
};

export default handleActions(
  {
    [changeCurrentWorkspace]: (state: WorkspacesStateType, { payload }) => ({
      ...state,
      currentWorkspaceUid: payload,
    }),
    [fetchWorkspaces]: (state: WorkspacesStateType) => ({
      ...state,
      loadingStatus: loadingStatuses.INITED,
    }),
    [setWorkspaces]: (state: WorkspacesStateType, { payload }) => {
      return {
        ...state,
        workspaces: payload,
        currentWorkspaceUid: updateWorkspaceUrl(payload),
        loadingStatus: loadingStatuses.LOADED,
      };
    },
    [changeWorkspace]: (state: WorkspacesStateType, { payload }) => ({
      ...state,
      workspaces: updateItemInListByIdHelper(state.workspaces, payload),
    }),
    [createWorkspace]: (state: WorkspacesStateType, { payload }) => ({
      ...state,
      workspaces: [...state.workspaces, payload],
    }),
    [deleteWorkspace]: (state: WorkspacesStateType, { payload }) => ({
      ...state,
      workspaces: state.workspaces.filter(({ id }) => id !== payload),
    }),
  },
  initialState
);
